import { Injectable } from '@angular/core';
import { filter, map, Observable, Subject, switchMap, take, throwError, withLatestFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '@environments/environment';
import { ResponseObjectSuccess, ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectAuthenticationIsReseller, selectAuthenticationIsSuperUser, selectCredential } from '@store/selectors/authentication.selectors';
import { MenuItem } from 'primeng/api';
import { getRoles, transformArrayModuleForm, transformArrayRoleModuleToModuleForm, transformDropdown } from '@core/helpers';
import { Credential, ModuloForm, Modulo, Rol } from '@core/models';
import { selectModuleList } from '@store/selectors';
import { Toggle } from '@shared/components/table/interfaces';
import { MenuItemExtended } from '@core/interfaces/menu-item-extended';
import { RolModuleEnum, RolModulePermissionEnum } from '@core/enums';
import { getPermissions } from '@core/helpers/general.helper';

@Injectable({
  providedIn: 'root',
})
export class ModuleService {
  credential$: Observable<Credential | null>;
  moduleList$: Observable<Modulo[]>;

  destroyTab$ = new Subject<void>();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private findAllCompletedSource = new Subject<void>();
  findAllCompleted$ = this.findAllCompletedSource.asObservable();

  private changeTabSource = new Subject<MenuItemExtended|null>();
  changeTab$ = this.changeTabSource.asObservable();

  private changeChannelEnabledSource = new Subject<Toggle<ModuloForm>>();
  changeChannelEnabledTab$ = this.changeChannelEnabledSource.asObservable();

  private toggleDialogSource = new Subject<boolean>();
  toggleDialog$ = this.toggleDialogSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.moduleList$ = this.store.select(selectModuleList);
  }

  onDestroyTab(): void {
    this.destroyTab$.next();
    this.destroyTab$.complete();
    this.destroyTab$ = new Subject<void>();
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeDialog();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  findAllCompleted(): void {
    this.findAllCompletedSource.next();
  }

  changeTab(event: MenuItemExtended): void {
    setTimeout(() => this.changeTabSource.next(event));
  }

  changeChannelEnabled(event: Toggle<ModuloForm>): void {
    this.changeChannelEnabledSource.next(event);
  }

  toggleDialog(event: boolean): void {
    this.toggleDialogSource.next(event);
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseObjectSuccess<Modulo>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));
        
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseObjectSuccess<Modulo>>(
          `${environment.microSeguridades}/api/v1/pos/modulos/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findAll(): Observable<ResponseSuccess<Modulo>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential.token && !credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Modulo>>(
          `${environment.microSeguridades}/api/v1/pos/modulos`,
          {
            headers: { Authorization: token },
            params: {
              esJuvoAdmin: true,
              // accountId: credential!.idCuenta!,
            }
          },
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.moduleList$.pipe(
      filter((modules): modules is Modulo[] => !!modules),
      map((modules) => transformDropdown(modules, 'idModulo', 'nombre')),
    );
  }

  generateModulePermissions({ modules: currentModules }: Rol): Observable<ModuloForm[]> {
    return this.moduleList$.pipe(
      withLatestFrom(
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      filter(([ modules ]) => !!modules && !!modules?.length),
      map(([ modules, isSuperUser, isReseller ]) => ({ modules: modules as Modulo[], isSuperUser, isReseller })),
      map(({ modules, isSuperUser, isReseller }) =>  getRoles({ isSuperUser, isReseller, modules: transformArrayModuleForm(modules), currentModules: transformArrayRoleModuleToModuleForm(currentModules) })),
      take(1),
    ) 
  }

  generatePermissions(currentPermissions?: (RolModuleEnum | RolModulePermissionEnum)[]): Observable<(RolModuleEnum | RolModulePermissionEnum)[]> {
    return this.store.select(selectAuthenticationIsSuperUser).pipe(
      withLatestFrom(
        this.store.select(selectAuthenticationIsReseller)
      ),
      map(([ isSuperUser, isReseller ]) => ({ isSuperUser, isReseller })),
      map(({ isSuperUser, isReseller }) =>  getPermissions({ isSuperUser, isReseller, currentPermissions: currentPermissions || [] })),
      take(1),
    ) 
  }
}
